import React from "react"
import { motion } from "motion/react"

/**
 * The question a draft asks when the checkin the user described could be more
 * than one of them.
 *
 * "Append this to my Loft Story checkin" is unambiguous to the person who wrote
 * both of them and ambiguous to everything else. The resolver inside the
 * compiler will not guess, so instead of a proposal the draft carries the
 * candidates and this panel renders them as a choice. Picking one sends an
 * ordinary chat message naming its id — the draft is pending, so the only tools
 * on the model's menu are the ones that revise it, and the pick arrives as
 * feedback that becomes the proposal the user then approves.
 */

export interface DisambiguationCandidateView {
    checkin_id: number
    applies_to: string
    body_preview: string
}

export interface DisambiguationView {
    description: string
    reason: string
    candidates: DisambiguationCandidateView[]
}

interface CheckinDisambiguationPanelProps {
    disambiguation: DisambiguationView
    formatDate: (dateString: string, includeYearIfDifferent?: boolean) => string
    /** Send the user's pick as a chat message. Absent once the choice is stale. */
    onPick?: (message: string) => void
}

/**
 * What picking a candidate says. The id is what the revision needs to name, and
 * the date is what makes the message readable in the transcript afterwards.
 */
export const pickMessage = (
    candidate: DisambiguationCandidateView,
    formatDate: (dateString: string, includeYearIfDifferent?: boolean) => string,
): string => `Use checkin ${candidate.checkin_id}, the one from ${formatDate(candidate.applies_to, true)}.`

const CheckinDisambiguationPanel: React.FC<CheckinDisambiguationPanelProps> = ({
    disambiguation,
    formatDate,
    onPick,
}) => {
    const { candidates } = disambiguation

    return (
        <motion.div
            className="mb-4"
            initial={{ opacity: 0, scale: 0.95, y: -8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ type: 'spring', stiffness: 500, damping: 30 }}
            style={{ transformOrigin: 'top center' }}
        >
            <div className="text-gray-600 italic mb-1">
                {candidates.length > 0
                    ? "Which one did you mean?"
                    : "I couldn't find the checkin you meant."}
            </div>
            <div className="text-sm text-gray-500 mb-4">{disambiguation.reason}</div>

            <div className="space-y-3 mb-2">
                {candidates.map(candidate => (
                    <button
                        key={candidate.checkin_id}
                        type="button"
                        disabled={!onPick}
                        onClick={() => onPick?.(pickMessage(candidate, formatDate))}
                        className="w-full text-left sm:border-l-4 pl-3 sm:pl-4 py-3 sm:border-dashed sm:border-gray-400 hover:bg-gray-50 disabled:hover:bg-transparent disabled:cursor-default rounded-r"
                    >
                        <div className="text-sm font-medium text-gray-700 mb-1">
                            {formatDate(candidate.applies_to, true)}
                        </div>
                        <div className="text-sm text-gray-600 whitespace-pre-wrap">
                            {candidate.body_preview}
                        </div>
                    </button>
                ))}
            </div>
        </motion.div>
    )
}

export default CheckinDisambiguationPanel
